import { ref, Ref, onMounted } from 'vue';
import api from '@/services/api';

interface Rating {
  id: string;
  fromUserId: string;
  toUserId: string;
  isLike: boolean;
  comment: string;
  reply: string | null;
  productId: string;
  ratingType: 'seller' | 'buyer';
  createdAt: string;
  updatedAt: string;
  fromUser: {
    id: string;
    username: string;
  };
  product: {
    id: string;
    name: string;
    slug: string;
  };
}

interface RatingStats {
  seller: {
    likes: number;
    dislikes: number;
  };
  buyer: {
    likes: number;
    dislikes: number;
  };
}

interface Meta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface RatingsResponse {
  success: boolean;
  data: {
    data: Rating[];
    meta: Meta;
  };
}

interface StatsResponse {
  success: boolean;
  data: RatingStats;
}

interface ApiError {
  response?: {
    status: number;
    data?: {
      message?: string;
    };
  };
}

export function useUserRatings(userId: Ref<string | null>) {
  const ratings = ref<Rating[]>([]);
  const stats = ref<RatingStats | null>(null);
  const loading = ref(false);
  const statsLoading = ref(false);
  const error = ref<string | null>(null);
  const authRequired = ref(false);
  const meta = ref<Meta | null>(null);
  const currentPage = ref(1);
  const userRole = ref<'seller' | 'buyer'>('seller');

  // Helper function to check if error is auth-related
  const isAuthError = (error: unknown): boolean => {
    const apiError = error as ApiError;

    if (apiError?.response) {
      const status = apiError.response.status;
      const message = apiError.response.data?.message || '';

      // Check for 500 with "no token provided" or similar auth messages
      if (
        status === 500 &&
        (message.toLowerCase().includes('no token') ||
          message.toLowerCase().includes('token') ||
          message.toLowerCase().includes('unauthorized') ||
          message.toLowerCase().includes('authentication'))
      ) {
        return true;
      }

      // Also check for standard auth error codes
      if (status === 401 || status === 403) {
        return true;
      }
    }

    return false;
  };

  const fetchRatings = async (
    page: number = 1,
    role: 'seller' | 'buyer' = 'seller'
  ) => {
    if (!userId.value) return;

    loading.value = true;
    error.value = null;
    authRequired.value = false;

    try {
      const response = await api.get<RatingsResponse>(
        `/api/ratings/user/${userId.value}?userRole=${role}&page=${page}&limit=6`
      );

      if (response.data.success) {
        ratings.value = response.data.data.data;
        meta.value = response.data.data.meta;
        currentPage.value = page;
        userRole.value = role;
      } else {
        error.value = 'Greška prilikom učitavanja ocena.';
      }
    } catch (err: unknown) {
      console.error('Error fetching user ratings:', err);

      const apiError = err as ApiError;
      console.error('Error response:', apiError?.response);
      console.error('Error status:', apiError?.response?.status);
      console.error('Error message:', apiError?.response?.data?.message);

      if (isAuthError(err)) {
        console.log('Auth error detected, setting authRequired to true');
        authRequired.value = true;
        error.value = null; // Clear error since we're showing auth message
      } else {
        error.value = 'Došlo je do greške prilikom komunikacije sa serverom.';
      }
    } finally {
      loading.value = false;
    }
  };

  const fetchRatingStats = async () => {
    if (!userId.value) return;

    statsLoading.value = true;

    try {
      const response = await api.get<StatsResponse>(
        `/api/ratings/stats/${userId.value}`
      );

      if (response.data.success) {
        stats.value = response.data.data;
      }
    } catch (err: unknown) {
      console.error('Error fetching rating stats:', err);

      if (isAuthError(err)) {
        authRequired.value = true;
      }
    } finally {
      statsLoading.value = false;
    }
  };

  const changePage = (page: number) => {
    if (
      page >= 1 &&
      meta.value &&
      page <= meta.value.totalPages &&
      page !== currentPage.value
    ) {
      fetchRatings(page, userRole.value);
    }
  };

  const changeUserRole = (role: 'seller' | 'buyer') => {
    if (role !== userRole.value) {
      fetchRatings(1, role);
    }
  };

  // Watch for userId changes
  onMounted(() => {
    if (userId.value) {
      fetchRatings();
      fetchRatingStats();
    }
  });

  return {
    ratings,
    stats,
    loading,
    statsLoading,
    error,
    authRequired,
    meta,
    currentPage,
    userRole,
    fetchRatings,
    fetchRatingStats,
    changePage,
    changeUserRole,
  };
}
